import React, { useEffect, useRef, useState } from "react";
import OwnerLayout from "@/components/OwnerLayout";
import { api, fileUrl } from "@/lib/api";
import { Upload, Image as ImageIcon, QrCode, Store, Clock } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const defaultHours = () => Array.from({ length: 7 }, (_, d) => ({ day: d, open: "08:00", close: "22:00", closed: false }));

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [name, setName] = useState("");
  const [bank, setBank] = useState("");
  const [hours, setHours] = useState(defaultHours());
  const logoRef = useRef();
  const qrisRef = useRef();

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setName(data.restaurant_name || "");
    setBank(data.bank_account_name || "");
    const h = data.opening_hours && data.opening_hours.length === 7 ? data.opening_hours : defaultHours();
    setHours(h);
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd);
    return data.path;
  };

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const path = await upload(f);
      await api.put("/settings", { logo_path: path });
      toast.success("Logo diperbarui");
      load();
    } catch { toast.error("Gagal mengunggah"); }
  };

  const onQris = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const path = await upload(f);
      await api.put("/settings", { qris_path: path });
      toast.success("QRIS diperbarui");
      load();
    } catch { toast.error("Gagal mengunggah"); }
  };

  const saveMeta = async () => {
    await api.put("/settings", { restaurant_name: name, bank_account_name: bank });
    toast.success("Tersimpan");
    load();
  };

  const saveHours = async () => {
    await api.put("/settings", { opening_hours: hours });
    toast.success("Jam buka tersimpan");
    load();
  };

  const updateHour = (d, patch) => {
    setHours(hours.map((h) => h.day === d ? { ...h, ...patch } : h));
  };

  if (!settings) return <OwnerLayout><div className="text-[#5C6A61]">Memuat...</div></OwnerLayout>;

  return (
    <OwnerLayout>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-[#1E3A2B]">Pengaturan</h1>
        <p className="text-[#5C6A61] mt-1">Atur identitas restoran, QRIS, dan jam buka</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-md border border-[#D3CEC4] p-6">
          <h2 className="font-display text-lg font-semibold text-[#1E3A2B] mb-4 flex items-center gap-2"><Store className="w-4 h-4" /> Identitas Restoran</h2>
          <label className="block mb-3">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Nama Restoran</span>
            <input data-testid="input-restaurant-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
          </label>
          <label className="block mb-4">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Nama Rekening/Penerima QRIS</span>
            <input data-testid="input-bank-name" value={bank} onChange={(e) => setBank(e.target.value)} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" placeholder="Nama yang muncul di struk" />
          </label>
          <button data-testid="btn-save-meta" onClick={saveMeta} className="bg-[#C25934] text-white px-5 py-2.5 rounded-xl font-medium">Simpan</button>

          <div className="mt-6">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Logo Restoran</span>
            <div className="mt-2 flex items-center gap-3">
              <div className="w-20 h-20 rounded-md bg-[#EAE7DF] overflow-hidden flex items-center justify-center">
                {settings.logo_path ? <img src={fileUrl(settings.logo_path)} alt="logo" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-[#5C6A61]/40" />}
              </div>
              <div>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="input-logo-file" />
                <button onClick={() => logoRef.current?.click()} className="bg-[#EAE7DF] text-[#1E3A2B] px-3 py-2 rounded-xl text-sm inline-flex items-center gap-2" data-testid="btn-upload-logo">
                  <Upload className="w-4 h-4" /> Unggah Logo
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-md border border-[#D3CEC4] p-6">
          <h2 className="font-display text-lg font-semibold text-[#1E3A2B] mb-2 flex items-center gap-2"><QrCode className="w-4 h-4" /> QRIS Pembayaran</h2>
          <p className="text-sm text-[#5C6A61] mb-4">Unggah gambar QRIS statis milik rekening/toko Anda.</p>
          <div className="aspect-square w-full max-w-xs bg-[#EAE7DF] rounded-md overflow-hidden flex items-center justify-center mb-4">
            {settings.qris_path ? <img src={fileUrl(settings.qris_path)} alt="QRIS" className="w-full h-full object-contain" /> : <QrCode className="w-14 h-14 text-[#5C6A61]/40" />}
          </div>
          <input ref={qrisRef} type="file" accept="image/*" className="hidden" onChange={onQris} data-testid="input-qris-file" />
          <button data-testid="btn-upload-qris" onClick={() => qrisRef.current?.click()} className="bg-[#C25934] text-white px-4 py-2.5 rounded-xl text-sm font-medium inline-flex items-center gap-2">
            <Upload className="w-4 h-4" /> Unggah QRIS
          </button>
        </div>

        <div className="bg-white rounded-md border border-[#D3CEC4] p-6 lg:col-span-2">
          <h2 className="font-display text-lg font-semibold text-[#1E3A2B] mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> Jam Buka</h2>
          <p className="text-sm text-[#5C6A61] mb-4">Di luar jam buka, pelanggan tetap bisa melihat menu tapi tidak bisa membuat pesanan.</p>
          <div className="divide-y divide-[#EAE7DF]" data-testid="opening-hours-editor">
            {hours.map((h) => (
              <div key={h.day} className="flex items-center gap-3 py-2.5 flex-wrap">
                <div className="w-20 text-sm text-[#1E3A2B] font-medium">{DAYS[h.day]}</div>
                <label className="flex items-center gap-2 text-xs text-[#5C6A61]">
                  <input
                    type="checkbox"
                    data-testid={`chk-closed-${h.day}`}
                    checked={!h.closed}
                    onChange={(e) => updateHour(h.day, { closed: !e.target.checked })}
                  />
                  Buka
                </label>
                {!h.closed && (
                  <>
                    <input
                      type="time"
                      data-testid={`input-open-${h.day}`}
                      value={h.open}
                      onChange={(e) => updateHour(h.day, { open: e.target.value })}
                      className="border border-[#D3CEC4] rounded-md px-2 py-1 text-sm"
                    />
                    <span className="text-[#5C6A61] text-sm">—</span>
                    <input
                      type="time"
                      data-testid={`input-close-${h.day}`}
                      value={h.close}
                      onChange={(e) => updateHour(h.day, { close: e.target.value })}
                      className="border border-[#D3CEC4] rounded-md px-2 py-1 text-sm"
                    />
                  </>
                )}
                {h.closed && <span className="text-xs text-[#D64045] uppercase tracking-wider">Tutup</span>}
              </div>
            ))}
          </div>
          <button data-testid="btn-save-hours" onClick={saveHours} className="mt-4 bg-[#C25934] text-white px-5 py-2.5 rounded-xl font-medium">Simpan Jam Buka</button>
        </div>
      </div>
    </OwnerLayout>
  );
}
